import { motion } from "framer-motion";
import { Mic, Phone, PhoneOff, Volume2, Grid3X3, Star } from "lucide-react";
import { WhatsAppIcon } from "./BrandIcons";

/* ---------- Base stylized 3D-look robot (vector = lightweight on mobile) ---------- */

export const RobotBase = ({ accent = "#22D3EE", headset = false, size = 120 }) => (
    <motion.svg
        width={size}
        height={size * 1.18}
        viewBox="0 0 220 260"
        animate={{ y: [0, -9, 0], rotate: [0, -1.2, 0, 1.2, 0] }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
        style={{ filter: `drop-shadow(0 18px 24px rgba(0,0,0,0.55)) drop-shadow(0 0 22px ${accent}33)` }}
        aria-hidden="true"
    >
        <defs>
            <linearGradient id="rb-body" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#334155" />
                <stop offset="0.5" stopColor="#1E293B" />
                <stop offset="1" stopColor="#0B1220" />
            </linearGradient>
            <linearGradient id="rb-face" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#0F1B30" />
                <stop offset="1" stopColor="#060B16" />
            </linearGradient>
            <radialGradient id="rb-glow" cx="0.5" cy="0.4" r="0.7">
                <stop offset="0" stopColor={accent} stopOpacity="0.9" />
                <stop offset="1" stopColor={accent} stopOpacity="0" />
            </radialGradient>
        </defs>

        {/* ground shadow */}
        <ellipse cx="110" cy="248" rx="52" ry="9" fill="#000" opacity="0.5" />
        {/* antenna */}
        <line x1="110" y1="34" x2="110" y2="14" stroke="#475569" strokeWidth="4" strokeLinecap="round" />
        <circle cx="110" cy="12" r="7" fill="url(#rb-glow)" />
        <circle cx="110" cy="12" r="3.5" fill={accent} />
        {/* head */}
        <rect x="55" y="32" width="110" height="82" rx="26" fill="url(#rb-body)" stroke="#475569" strokeWidth="1.5" />
        <rect x="67" y="46" width="86" height="54" rx="18" fill="url(#rb-face)" />
        {/* eyes */}
        <motion.circle
            cx="92" cy="72" r="8" fill={accent}
            animate={{ opacity: [1, 1, 0.15, 1, 1] }}
            transition={{ duration: 4, repeat: Infinity, times: [0, 0.9, 0.95, 0.98, 1] }}
        />
        <motion.circle
            cx="128" cy="72" r="8" fill={accent}
            animate={{ opacity: [1, 1, 0.15, 1, 1] }}
            transition={{ duration: 4, repeat: Infinity, times: [0, 0.9, 0.95, 0.98, 1] }}
        />
        <circle cx="94.5" cy="69.5" r="2.4" fill="#fff" opacity="0.9" />
        <circle cx="130.5" cy="69.5" r="2.4" fill="#fff" opacity="0.9" />
        <path d="M100 90 q10 7 20 0" stroke={accent} strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.85" />
        {/* headset */}
        {headset && (
            <g>
                <path d="M52 74 Q52 22 110 22 Q168 22 168 74" stroke="#0F172A" strokeWidth="11" fill="none" strokeLinecap="round" />
                <path d="M52 74 Q52 22 110 22 Q168 22 168 74" stroke="#64748B" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.6" />
                <rect x="42" y="62" width="18" height="34" rx="9" fill="#0F172A" stroke={accent} strokeWidth="2" />
                <rect x="160" y="62" width="18" height="34" rx="9" fill="#0F172A" stroke={accent} strokeWidth="2" />
                <path d="M169 90 Q172 116 148 122" stroke="#64748B" strokeWidth="5" fill="none" strokeLinecap="round" />
                <circle cx="146" cy="122" r="6" fill={accent} />
            </g>
        )}
        {/* neck + torso */}
        <rect x="99" y="114" width="22" height="14" rx="5" fill="#475569" />
        <rect x="64" y="128" width="92" height="96" rx="24" fill="url(#rb-body)" stroke="#475569" strokeWidth="1.5" />
        {/* chest core */}
        <circle cx="110" cy="168" r="20" fill="#0B1220" stroke={accent} strokeWidth="2" />
        <motion.circle
            cx="110" cy="168" r="11" fill={accent}
            animate={{ opacity: [0.65, 1, 0.65] }}
            transition={{ duration: 2.2, repeat: Infinity }}
        />
        <path d="M104 162 l4 12 2-6 2 6 4-12" stroke="#0B1220" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        {/* arms */}
        <rect x="38" y="136" width="20" height="62" rx="10" fill="url(#rb-body)" stroke="#475569" />
        <rect x="162" y="136" width="20" height="62" rx="10" fill="url(#rb-body)" stroke="#475569" />
        <circle cx="48" cy="204" r="11" fill="#1E293B" stroke={accent} strokeWidth="1.5" />
        <circle cx="172" cy="204" r="11" fill="#1E293B" stroke={accent} strokeWidth="1.5" />
        {/* belly vents */}
        <line x1="88" y1="202" x2="132" y2="202" stroke="#334155" strokeWidth="4" strokeLinecap="round" />
        <line x1="92" y1="212" x2="128" y2="212" stroke="#334155" strokeWidth="4" strokeLinecap="round" />
    </motion.svg>
);

const Waveform = ({ color = "#22D3EE", bars = 9, height = 34 }) => (
    <div className="flex items-center gap-[3px]" style={{ height }} aria-hidden="true">
        {Array.from({ length: bars }).map((_, i) => (
            <span
                key={i}
                className="wave-bar w-[3px] rounded-full"
                style={{
                    height: "100%",
                    background: color,
                    animationDelay: `${i * 0.12}s`,
                    animationDuration: `${0.9 + (i % 3) * 0.2}s`,
                }}
            />
        ))}
    </div>
);

const FloatCard = ({ className = "", delay = 0, children, testid }) => (
    <motion.div
        data-testid={testid}
        className={`glass absolute rounded-2xl p-3 ${className}`}
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay, duration: 0.5 }}
    >
        <div className="animate-floaty" style={{ animationDelay: `${delay + 0.6}s` }}>
            {children}
        </div>
    </motion.div>
);

/* ---------- Service visuals ---------- */

export const CallVisual = ({ compact = false }) => (
    <div className="relative flex h-full w-full items-center justify-center" data-testid="visual-calling">
        <RobotBase accent="#22D3EE" headset size={compact ? 96 : 128} />
        <FloatCard className="right-0 top-2 w-44 sm:w-48" delay={0.25} testid="call-interface-card">
            <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 font-display text-xs font-bold text-white">AK</div>
                <div className="min-w-0">
                    <p className="truncate text-[11px] font-semibold text-white">Ananya — Hot Lead</p>
                    <p className="font-mono2 text-[9px] text-emerald-400">● AI Call Live · 02:14</p>
                </div>
            </div>
            <div className="mt-2.5 flex justify-center rounded-lg bg-black/30 py-2">
                <Waveform />
            </div>
            <div className="mt-2.5 flex items-center justify-center gap-2.5 text-slate-400">
                <Mic className="h-3.5 w-3.5" />
                <Grid3X3 className="h-3.5 w-3.5" />
                <Volume2 className="h-3.5 w-3.5" />
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-500">
                    <PhoneOff className="h-3 w-3 text-white" />
                </span>
            </div>
        </FloatCard>
        <FloatCard className="bottom-1 left-0 flex items-center gap-2 px-3 py-2" delay={0.45} testid="call-qualified-chip">
            <Phone className="h-3.5 w-3.5 text-cyan-400" />
            <span className="text-[10px] font-semibold text-cyan-300">Lead qualified on call</span>
        </FloatCard>
    </div>
);

export const WhatsAppVisual = ({ compact = false }) => (
    <div className="relative flex h-full w-full items-center justify-center gap-2" data-testid="visual-whatsapp">
        <RobotBase accent="#25D366" size={compact ? 88 : 116} />
        <FloatCard className="right-0 top-1 w-40 sm:w-44 p-0 overflow-hidden" delay={0.25} testid="whatsapp-phone-card">
            <div className="flex items-center gap-2 bg-[#075E54] px-2.5 py-2">
                <WhatsAppIcon className="h-4 w-4" color="#fff" />
                <div>
                    <p className="text-[9px] font-bold leading-tight text-white notranslate">WorkAssign AI</p>
                    <p className="text-[7px] leading-tight text-emerald-200">online</p>
                </div>
            </div>
            <div className="space-y-1.5 bg-[#0B141A] p-2">
                <div className="ml-auto w-fit max-w-[90%] rounded-lg rounded-tr-none bg-[#005C4B] px-2 py-1 text-[8px] text-emerald-50">
                    Do you have a demo today?
                </div>
                <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.8 }}
                    className="w-fit max-w-[90%] rounded-lg rounded-tl-none bg-[#1F2C34] px-2 py-1 text-[8px] text-slate-200"
                >
                    Yes! I have 3 slots. Booking 4:30 PM for you ✓
                </motion.div>
            </div>
        </FloatCard>
    </div>
);

export const SupportVisual = ({ compact = false }) => (
    <div className="relative flex h-full w-full items-center justify-center" data-testid="visual-support">
        <RobotBase accent="#38BDF8" headset size={compact ? 96 : 124} />
        <FloatCard className="left-0 top-2 w-40" delay={0.3} testid="support-ticket-card">
            <p className="font-mono2 text-[9px] uppercase tracking-wider text-slate-500">Ticket #2481</p>
            <p className="mt-0.5 text-[11px] font-semibold text-white">Refund status query</p>
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[9px] font-bold text-emerald-400">✓ Resolved by AI</span>
        </FloatCard>
        <FloatCard className="bottom-1 right-0 px-3 py-2" delay={0.5} testid="support-sla-chip">
            <p className="text-[10px] font-semibold text-sky-300">Avg. reply: 4 seconds</p>
        </FloatCard>
    </div>
);

export const LeadGenVisual = ({ compact = false }) => (
    <div className="relative flex h-full w-full items-center justify-center" data-testid="visual-leadgen">
        <RobotBase accent="#F59E0B" size={compact ? 96 : 124} />
        <FloatCard className="right-0 top-1 w-44" delay={0.3} testid="leadgen-funnel-card">
            {[
                { label: "New Enquiry", w: "100%", cls: "bg-slate-500/30 text-slate-300" },
                { label: "Qualified", w: "78%", cls: "bg-blue-500/30 text-blue-300" },
                { label: "Hot Lead", w: "56%", cls: "bg-amber-500/30 text-amber-300" },
            ].map((f) => (
                <div key={f.label} className="mb-1.5 flex justify-center">
                    <span className={`rounded-md px-2 py-1 text-center text-[9px] font-semibold ${f.cls}`} style={{ width: f.w }}>
                        {f.label}
                    </span>
                </div>
            ))}
            <div className="mt-1 flex items-center justify-center gap-1 rounded-md bg-emerald-500/15 py-1">
                <Star className="h-3 w-3 text-emerald-400" />
                <span className="text-[9px] font-bold text-emerald-300">Score 92 · Book demo</span>
            </div>
        </FloatCard>
    </div>
);

export const CRMVisual = ({ compact = false }) => (
    <div className="relative flex h-full w-full items-center justify-center" data-testid="visual-crm">
        <motion.img
            src="/assets/robot-crm.png"
            alt="CRM assistant robot organising leads in one pipeline"
            data-testid="crm-robot-image"
            loading="lazy"
            className="animate-floaty w-auto object-contain"
            style={{
                height: compact ? 150 : 210,
                filter: "drop-shadow(0 18px 24px rgba(0,0,0,0.55)) drop-shadow(0 0 26px rgba(167,139,250,0.35))",
            }}
            animate={{ y: [0, -9, 0], rotate: [0, -1, 0, 1, 0] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
        />
    </div>
);

export const AutomationVisual = ({ compact = false }) => (
    <div className="relative flex h-full w-full items-center justify-center" data-testid="visual-automation">
        <RobotBase accent="#2DD4BF" size={compact ? 96 : 124} />
        <FloatCard className="right-0 top-1 w-44" delay={0.3} testid="automation-flow-card">
            <div className="flex items-center gap-1.5">
                {["Enquiry", "AI Reply", "Follow-up", "Booked"].map((n, i) => (
                    <div key={n} className="flex items-center gap-1.5">
                        <span className={`rounded-md px-1.5 py-1 text-[8px] font-bold ${i === 3 ? "bg-emerald-500/20 text-emerald-300" : "bg-cyan-500/15 text-cyan-300"}`}>
                            {n}
                        </span>
                        {i < 3 && <span className="text-[8px] text-slate-600">→</span>}
                    </div>
                ))}
            </div>
            <div className="mt-2 flex items-center justify-between rounded-md bg-black/25 px-2 py-1.5">
                <span className="text-[8px] text-slate-500">Runs automatically</span>
                <span className="flex gap-1">
                    <svg className="gear h-3.5 w-3.5 text-teal-400" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm9 4-.1 1.2 2 1.6-2 3.4-2.4-.9a8 8 0 0 1-2 1.2L16 21h-4l-.5-2.5a8 8 0 0 1-2-1.2l-2.4.9-2-3.4 2-1.6L7 12l.1-1.2-2-1.6 2-3.4 2.4.9a8 8 0 0 1 2-1.2L12 3h4l.5 2.5a8 8 0 0 1 2 1.2l2.4-.9 2 3.4-2 1.6z" transform="scale(0.9) translate(1.3 1.3)" />
                    </svg>
                    <svg className="gear gear-rev h-3.5 w-3.5 text-teal-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm9 4-.1 1.2 2 1.6-2 3.4-2.4-.9a8 8 0 0 1-2 1.2L16 21h-4l-.5-2.5a8 8 0 0 1-2-1.2l-2.4.9-2-3.4 2-1.6L7 12l.1-1.2-2-1.6 2-3.4 2.4.9a8 8 0 0 1 2-1.2L12 3h4l.5 2.5a8 8 0 0 1 2 1.2l2.4-.9 2 3.4-2 1.6z" transform="scale(0.9) translate(1.3 1.3)" />
                    </svg>
                </span>
            </div>
        </FloatCard>
    </div>
);

export const SERVICE_VISUALS = {
    calling: CallVisual,
    whatsapp: WhatsAppVisual,
    support: SupportVisual,
    leadgen: LeadGenVisual,
    crm: CRMVisual,
    automation: AutomationVisual,
};
